import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraSelect, SpecraText, specraButtonVariants } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/authContext';
import { useProjectContext } from '../../contexts/ProjectContext';
import { useConvertToPreAward } from '../../hooks/useProjects';
import { useView } from '../../lib/access/useView';
import { proposalSubmissionClient } from '../../lib/connect/clients';
export function PreAwardHandoffPanel({ proposalProjectId }) {
    const { user } = useAuth();
    const { projectId, role } = useProjectContext();
    const view = useView({
        kind: 'project',
        id: proposalProjectId,
        objectType: 'delivery.programs',
        objectId: proposalProjectId
    }, {
        action: 'convert_to_pre_award',
        enabled: !!proposalProjectId
    });
    const allowed = !!user?.tenant_id && projectId === proposalProjectId && !!role && !view.isLoading && view.viewMode !== 'no_view' && view.can('convert_to_pre_award');
    const history = useQuery({
        queryKey: [
            'proposal-handoff-submissions',
            user?.tenant_id,
            user?.id,
            proposalProjectId
        ],
        queryFn: async ()=>(await proposalSubmissionClient.listProposalSubmissions({
                projectId: proposalProjectId
            })).submissions,
        enabled: allowed,
        retry: false,
        gcTime: 0
    });
    const [submissionId, setSubmissionId] = useState('');
    const lock = useRef(false);
    const send = useConvertToPreAward();
    const rows = history.isError ? [] : (history.data ?? []).filter((row)=>row.projectId === proposalProjectId);
    const selected = rows.find((row)=>row.id === submissionId);
    const valid = allowed && !!selected && !history.isFetching && !history.isError && !send.isPending;
    async function submit() {
        if (!valid || lock.current) return;
        lock.current = true;
        try {
            await send.mutateAsync({
                proposalProjectId,
                submissionId
            });
        } catch  {
        /* Keep the mutation error beside the retained selection. */ } finally{
            lock.current = false;
        }
    }
    if (view.isLoading) return /*#__PURE__*/ _jsx("output", {
        children: /*#__PURE__*/ _jsx(SpecraText, {
            children: "Checking sending access…"
        })
    });
    if (!allowed) return /*#__PURE__*/ _jsx(SpecraText, {
        children: "Sending requires current Proposal membership and Growth handoff permission. It does not grant receiving access."
    });
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-4",
        "data-testid": Testids.proposalHandoff.sendPanel,
        children: [
            /*#__PURE__*/ _jsx(SpecraText, {
                className: "block",
                children: "Select an actual recorded submission to freeze for Pre-Award. Retrying that submission reuses its original handoff; a newer descendant retains the earlier history. Other Proposals and RFI history are unchanged."
            }),
            history.isFetching && /*#__PURE__*/ _jsx("output", {
                children: /*#__PURE__*/ _jsx(SpecraText, {
                    children: "Loading recorded submissions…"
                })
            }),
            history.isError && /*#__PURE__*/ _jsx(SpecraText, {
                role: "alert",
                children: history.error.message
            }),
            /*#__PURE__*/ _jsxs(SpecraSelect, {
                "aria-label": "Recorded submission",
                "data-testid": Testids.proposalHandoff.submissionSelect,
                value: submissionId,
                disabled: history.isFetching || history.isError || send.isPending,
                onChange: (event)=>{
                    setSubmissionId(event.target.value);
                    send.reset();
                },
                children: [
                    /*#__PURE__*/ _jsx("option", {
                        value: "",
                        children: "Select a recorded submission"
                    }),
                    rows.map((row)=>/*#__PURE__*/ _jsxs("option", {
                            value: row.id,
                            children: [
                                row.id,
                                " · ",
                                row.supersedesId ? `Revises ${row.supersedesId}` : 'Original',
                                " ·",
                                ' ',
                                row.submittedBy
                            ]
                        }, row.id))
                ]
            }),
            !history.isFetching && !history.isError && rows.length === 0 && /*#__PURE__*/ _jsx(SpecraText, {
                children: "No actual submissions recorded. Record a submission in Submission history first."
            }),
            selected && /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2 break-words",
                "data-testid": Testids.proposalHandoff.selectedSubmission,
                "data-submission-id": selected.id,
                children: [
                    /*#__PURE__*/ _jsxs(SpecraText, {
                        className: "block",
                        children: [
                            "Submission: ",
                            selected.id,
                            " · Submitted by ",
                            selected.submittedBy,
                            " ·",
                            ' ',
                            selected.submittedAt?.toDate().toISOString(),
                            " · ",
                            selected.method,
                            " ·",
                            ' ',
                            selected.recipient
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(SpecraText, {
                        className: "block",
                        children: [
                            "Pricing version: ",
                            selected.pricingVersionId,
                            " · Staffing version:",
                            ' ',
                            selected.staffingVersionId,
                            " · Solicitation version: ",
                            selected.solicitationVersionId
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        className: "block whitespace-pre-wrap",
                        children: selected.snapshotJson || 'Recorded snapshot unavailable.'
                    })
                ]
            }),
            send.isError && /*#__PURE__*/ _jsx(SpecraText, {
                role: "alert",
                children: send.error.message
            }),
            send.data && /*#__PURE__*/ _jsxs("output", {
                className: "block space-y-2",
                "data-testid": Testids.proposalHandoff.sendResult,
                "data-pre-award-id": send.data.summary?.preAwardProjectId,
                "data-handoff-id": send.data.handoff?.id,
                children: [
                    /*#__PURE__*/ _jsxs(SpecraText, {
                        className: "block",
                        children: [
                            "Sent submission ",
                            send.data.handoff?.submissionId,
                            ". Handoff ",
                            send.data.handoff?.id,
                            " ·",
                            ' ',
                            send.data.handoff?.frozenAt,
                            ". Receiving workspace:",
                            ' ',
                            send.data.summary?.preAwardProjectId,
                            "."
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        className: "block",
                        children: "Receiving access is assigned independently under Programs. Sending does not grant membership, Program Lead identity or PM approval authority."
                    }),
                    /*#__PURE__*/ _jsx(Link, {
                        className: specraButtonVariants({
                            variant: 'outline'
                        }),
                        "data-testid": Testids.proposalHandoff.openReceiver,
                        to: `/delivery/pre-award/${encodeURIComponent(send.data.summary.preAwardProjectId)}/v2?screen=baseline`,
                        children: "Open Pre-Award"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": Testids.proposalHandoff.sendConfirm,
                disabled: !valid,
                onClick: ()=>void submit(),
                children: send.isPending ? 'Sending…' : 'Send selected submission'
            }),
            history.isError && /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "outline",
                onClick: ()=>void history.refetch(),
                children: "Retry recorded submissions"
            })
        ]
    });
}
